import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { MessageResponse } from '@/components/ai-elements/message';
import { cn } from '@/lib/utils';
import type { ActiveSourceReference } from '../chat-experience';
import { getDocumentContent, getDocumentPreviewExcerpt } from '../document-artifact';
import { DocxPreviewRenderer } from './DocxPreviewRenderer';

type DocxPreviewBinding = {
  blob: Blob | null;
  error?: string | null;
  onRetry?: () => void;
  status: 'idle' | 'generating' | 'ready' | 'error';
};

type ChatDocumentPreviewProps = {
  activeReference?: ActiveSourceReference | null;
  className?: string;
  content?: string;
  document: ChatDocumentDTO;
  docxPreview?: DocxPreviewBinding;
  sourceReferences?: ActiveSourceReference[];
  variant?: 'card' | 'panel';
};

function shouldRenderDocxPreview(
  variant: 'card' | 'panel',
  document: ChatDocumentDTO,
  docxPreview?: DocxPreviewBinding,
) {
  if (variant !== 'panel' || !docxPreview) {
    return false;
  }

  if (document.status !== 'ready') {
    return false;
  }

  return docxPreview.status !== 'idle';
}

export function ChatDocumentPreview({
  className,
  content,
  document,
  docxPreview,
  variant = 'panel',
}: ChatDocumentPreviewProps) {
  const resolvedContent = (content ?? (
    variant === 'card'
      ? getDocumentPreviewExcerpt(document)
      : getDocumentContent(document)
  )).trim();

  if (shouldRenderDocxPreview(variant, document, docxPreview) && docxPreview) {
    return (
      <div
        className={cn(
          'artifact-document-surface artifact-document-surface-panel relative h-full overflow-hidden bg-[#f1f3f5] dark:bg-neutral-900',
          className,
        )}
      >
        <DocxPreviewRenderer
          blob={docxPreview.blob}
          error={docxPreview.error}
          onRetry={docxPreview.onRetry}
          status={docxPreview.status}
        />
      </div>
    );
  }

  if (!resolvedContent) {
    return (
      <div
        className={cn(
          variant === 'card'
            ? 'artifact-document-surface artifact-document-surface-card flex min-h-44 items-center justify-center p-6 text-sm text-muted-foreground'
            : 'artifact-document-surface flex min-h-[24rem] items-center justify-center p-10 text-sm text-muted-foreground',
          className,
        )}
      >
        {document.status === 'streaming'
          ? 'Le document se construit en temps réel.'
          : 'Aucun contenu détaillé disponible pour ce document.'}
      </div>
    );
  }

  return (
    <div
      className={cn(
        variant === 'card'
          ? 'artifact-document-surface artifact-document-surface-card relative min-h-44 overflow-hidden'
          : 'artifact-document-surface artifact-document-surface-panel flex h-full flex-col overflow-hidden',
        className,
      )}
    >
      <div className={cn(
        'artifact-document-prose',
        variant === 'card'
          ? 'pointer-events-none p-6 text-[11px] leading-5'
          : 'min-h-full flex-1 p-10 text-[15px] leading-8',
      )}
      >
        <MessageResponse>{resolvedContent}</MessageResponse>
      </div>
      {variant === 'card' ? <div className="artifact-document-fade" aria-hidden="true" /> : null}
    </div>
  );
}
